import { MapPin, Phone, Mail, Facebook, Instagram } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Logo } from "./Logo";

export const Footer = () => {
  const { t, lang } = useLanguage();

  const navLinks = [
    { key: "home", href: "#home" },
    { key: "about", href: "#about" },
    { key: "services", href: "#services" },
    { key: "booking", href: "#booking" },
    { key: "faq", href: "#faq" },
    { key: "contact", href: "#contact" },
  ];

  const socials = [
    { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61594510493553", label: "Facebook", testid: "social-facebook" },
    { icon: Instagram, href: "https://www.instagram.com/zirsen.cz/", label: "Instagram", testid: "social-instagram" },
  ];

  const advisors = [
    { name: t.about.partner1Name, email: "raffaele.zirone@iallianz.cz", phone: "728 234 101", ico: "IČO: 11746858", testid: "footer-advisor-raffaele" },
    { name: t.about.partner2Name, email: "ondrej.senfeld@iallianz.cz", phone: "608 125 907", ico: "IČO: 19739842", testid: "footer-advisor-ondrej" },
  ];

  return (
    <footer id="contact" data-testid="footer" className="relative overflow-hidden border-t border-teal-500/15 bg-[#02050A]">
      <div className="pointer-events-none absolute -top-24 left-1/3 h-[380px] w-[560px] rounded-full blur-[150px]" style={{ background: "radial-gradient(circle, rgba(0,245,212,0.07) 0%, transparent 70%)" }} aria-hidden="true" />
      <div className="mesh-orb orb-a absolute -left-40 -top-24 h-[420px] w-[420px] rounded-full" style={{ background: "rgba(13,148,136,0.12)" }} aria-hidden="true" />
      <div className="mesh-orb orb-b absolute -right-32 bottom-0 h-[380px] w-[380px] rounded-full" style={{ background: "rgba(30,58,95,0.5)" }} aria-hidden="true" />
      <div className="footer-beam" aria-hidden="true" />
      <span className="hero-particle" style={{ left: "8%", top: "22%", width: 3, height: 3, animationDelay: "0.6s" }} aria-hidden="true" />
      <span className="hero-particle" style={{ left: "88%", top: "35%", width: 2, height: 2, animationDelay: "1.7s" }} aria-hidden="true" />
      <span className="hero-particle" style={{ left: "55%", top: "70%", width: 3, height: 3, animationDelay: "2.6s" }} aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-4">
          <div>
            <Logo variant="light" className="h-14" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-400">{t.footer.tagline[lang]}</p>
            <div className="mt-6 flex gap-3">
              {socials.map(({ icon: Icon, href, label, testid }) => (
                <a
                  key={testid}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={testid}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 text-slate-400 transition-all duration-200 hover:border-teal-400 hover:text-teal-300 hover:shadow-[0_0_16px_rgba(45,212,191,0.25)]"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-mono text-xs font-bold uppercase tracking-[0.15em] text-teal-300">{t.footer.contactTitle[lang]}</h3>
            <ul className="mt-5 space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-2.5">
                <MapPin size={15} className="mt-0.5 shrink-0 text-teal-400" />
                <span data-testid="footer-address">{t.footer.address[lang]}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone size={15} className="shrink-0 text-teal-400" />
                <a href="tel:+420608125907" data-testid="footer-phone" className="transition-colors hover:text-teal-300">+420 608 125 907</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail size={15} className="shrink-0 text-teal-400" />
                <a href="mailto:info@zirsen.cz" data-testid="footer-email" className="transition-colors hover:text-teal-300">info@zirsen.cz</a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-mono text-xs font-bold uppercase tracking-[0.15em] text-teal-300">{t.footer.teamTitle[lang]}</h3>
            <ul className="mt-5 space-y-4 text-sm text-slate-400">
              {advisors.map((a) => (
                <li key={a.testid} data-testid={a.testid}>
                  <p className="font-semibold text-slate-200">{a.name}</p>
                  <a href={`mailto:${a.email}`} className="text-xs transition-colors hover:text-teal-300">{a.email}</a>
                  <p>
                    <a href={`tel:+420${a.phone.replace(/\s/g, "")}`} className="text-xs transition-colors hover:text-teal-300">{a.phone}</a>
                  </p>
                  <p className="font-mono mt-0.5 text-[11px] text-slate-500">{a.ico}</p>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-mono text-xs font-bold uppercase tracking-[0.15em] text-teal-300">{t.footer.linksTitle[lang]}</h3>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-400">
              {navLinks.map((l) => (
                <li key={l.key}>
                  <a href={l.href} data-testid={`footer-nav-${l.key}`} className="transition-colors duration-200 hover:text-teal-300">
                    {t.nav[l.key][lang]}
                  </a>
                </li>
              ))}
            </ul>
            <h3 className="font-mono mt-8 text-xs font-bold uppercase tracking-[0.15em] text-teal-300">{t.footer.legalTitle[lang]}</h3>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
              <li><a href="/ochrana-osobnich-udaju" data-testid="footer-privacy-link" className="transition-colors hover:text-teal-300">{t.footer.privacy[lang]}</a></li>
              <li><a href="/obchodni-podminky" data-testid="footer-terms-link" className="transition-colors hover:text-teal-300">{t.footer.terms[lang]}</a></li>
            </ul>
          </div>
        </div>

        <div className="relative mt-14 overflow-hidden rounded-2xl border border-teal-500/20" data-testid="footer-map">
          <iframe
            title="ZIRSEN — mapa kanceláře"
            src="https://www.openstreetmap.org/export/embed.html?bbox=14.5050%2C50.6760%2C14.5700%2C50.6960&layer=mapnik&marker=50.6855%2C14.5376"
            className="map-futuristic h-64 w-full sm:h-72"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(0,245,212,0.10) 0%, transparent 60%)" }} aria-hidden="true" />
          <span className="pointer-events-none absolute left-3 top-3 h-5 w-5 border-l-2 border-t-2 border-teal-400/70" aria-hidden="true" />
          <span className="pointer-events-none absolute right-3 top-3 h-5 w-5 border-r-2 border-t-2 border-teal-400/70" aria-hidden="true" />
          <span className="pointer-events-none absolute bottom-3 left-3 h-5 w-5 border-b-2 border-l-2 border-teal-400/70" aria-hidden="true" />
          <span className="pointer-events-none absolute bottom-3 right-3 h-5 w-5 border-b-2 border-r-2 border-teal-400/70" aria-hidden="true" />
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" aria-hidden="true">
            <span className="relative flex h-4 w-4">
              <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-teal-400" />
              <span className="relative inline-flex h-4 w-4 rounded-full border-2 border-[#050B14] bg-teal-400 shadow-[0_0_16px_rgba(45,212,191,0.9)]" />
            </span>
          </div>
          <div className="absolute bottom-3 left-3 flex max-w-[65%] items-center gap-2.5 rounded-xl border border-teal-500/30 bg-[#050B14]/85 px-4 py-2.5 backdrop-blur-md" data-testid="footer-map-address">
            <MapPin size={15} className="shrink-0 text-teal-300" />
            <span className="truncate font-mono text-[11px] font-semibold tracking-wide text-teal-100">{t.footer.address[lang]}</span>
          </div>
          <a
            href="https://www.google.com/maps/search/?api=1&query=Hrn%C4%8D%C3%AD%C5%99sk%C3%A1+2456%2C+%C4%8Cesk%C3%A1+L%C3%ADpa"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-map-link"
            className="absolute bottom-3 right-3 rounded-xl border border-teal-500/30 bg-teal-500/90 px-4 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest text-[#052E2B] transition-colors hover:bg-teal-300"
          >
            {lang === "cs" ? "Navigovat" : "Navigate"}
          </a>
        </div>

        <div className="mt-14 border-t border-slate-800 pt-8">
          <p className="max-w-4xl text-xs leading-relaxed text-slate-500" data-testid="footer-disclaimer">
            {t.footer.disclaimer[lang]}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-500" data-testid="footer-copyright">{t.footer.rights[lang]}</p>
          </div>
        </div>

        <div aria-hidden="true" className="pointer-events-none relative mt-16 select-none overflow-hidden">
          <svg viewBox="0 0 600 120" className="mx-auto w-full opacity-50" role="presentation">
            <text
              x="300"
              y="102"
              textAnchor="middle"
              textLength="585"
              lengthAdjust="spacingAndGlyphs"
              className="font-display"
              style={{ fontSize: 120, fontWeight: 900, fill: "none", stroke: "rgba(45,212,191,0.55)", strokeWidth: 1.5 }}
            >
              ZIRSEN
            </text>
          </svg>
        </div>
      </div>
    </footer>
  );
};
